import React, { FC } from "react";

export interface SkeletonProps {
  className?: string;
  variant?: "text" | "circular" | "rectangular";
  width?: string | number;
  height?: string | number;
  count?: number;
}

const Skeleton: FC<SkeletonProps> = ({
  className = "",
  variant = "rectangular",
  width,
  height,
  count = 1,
}) => {
  const baseClass = "animate-pulse bg-slate-200 dark:bg-slate-700";

  const variantClass = {
    text: "rounded",
    circular: "rounded-full",
    rectangular: "rounded-lg",
  }[variant];

  const style: React.CSSProperties = {
    width: width || "100%",
    height: height || (variant === "text" ? "1em" : "100%"),
  };

  if (count === 1) {
    return (
      <div
        className={`${baseClass} ${variantClass} ${className}`}
        style={style}
      />
    );
  }

  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className={`${baseClass} ${variantClass} ${className}`}
          style={style}
        />
      ))}
    </>
  );
};

/**
 * Pre-built skeleton for a product card in the menu grid.
 */
export const ProductCardSkeleton: FC<{ className?: string }> = ({
  className = "",
}) => {
  return (
    <div className={`flex flex-col ${className}`}>
      <Skeleton
        variant="rectangular"
        className="w-full aspect-square rounded-xl"
      />
      <div className="space-y-2 mt-3 px-1">
        <Skeleton variant="text" height="1rem" width="75%" />
        <Skeleton variant="text" height="0.875rem" width="40%" />
        <Skeleton variant="text" height="0.75rem" width="90%" />
      </div>
    </div>
  );
};

/**
 * Pre-built skeleton for a cart item row.
 */
export const CartItemSkeleton: FC = () => {
  return (
    <div className="flex items-center gap-3 p-4">
      <Skeleton variant="rectangular" width={60} height={60} className="rounded-lg flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton variant="text" height="1rem" width="60%" />
        <Skeleton variant="text" height="0.75rem" width="40%" />
      </div>
      <Skeleton variant="text" height="1rem" width={50} />
    </div>
  );
};

export default Skeleton;
